/**
 * Håller sessionen vid liv medan användaren arbetar.
 * Utloggning sker efter inaktivitet, inte efter en fast klocktid.
 */

import { useEffect, useRef, useCallback } from 'react';
import Cookies from 'js-cookie';
import { noteSessionActivity, refreshSession } from '@/services/api/client';
import { useAuthStore } from '@/store/authStore';
import {
  SESSION_INACTIVITY_LOGOUT_MS,
  shouldRefreshSession,
} from '@/utils/sessionActivity';

export function useActivityRefresh() {
  const lastRefreshRef = useRef<number>(Date.now());
  const lastActivityRef = useRef<number>(Date.now());
  const refreshIntervalRef = useRef<number | null>(null);
  const inactivityTimeoutRef = useRef<number | null>(null);
  const { user, logOutUser } = useAuthStore();

  const refreshToken = useCallback(async () => {
    if (Cookies.get('dqSession') !== '1' && !Cookies.get('refreshToken')) {
      return false;
    }
    const renewed = await refreshSession();
    if (renewed) lastRefreshRef.current = Date.now();
    return Boolean(renewed);
  }, []);

  const scheduleInactivityLogout = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    inactivityTimeoutRef.current = window.setTimeout(() => {
      logOutUser();
      window.location.href = '/login';
    }, SESSION_INACTIVITY_LOGOUT_MS);
  }, [logOutUser]);

  const handleActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    noteSessionActivity();
    scheduleInactivityLogout();

    if (shouldRefreshSession(0, now - lastRefreshRef.current)) {
      void refreshToken();
    }
  }, [refreshToken, scheduleInactivityLogout]);

  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    let throttleTimeout: number | null = null;
    const throttledActivity = () => {
      if (throttleTimeout) return;
      throttleTimeout = window.setTimeout(() => {
        throttleTimeout = null;
        handleActivity();
      }, 1000);
    };

    events.forEach(event => {
      window.addEventListener(event, throttledActivity, { passive: true });
    });

    handleActivity();

    refreshIntervalRef.current = window.setInterval(() => {
      const now = Date.now();
      const idleMs = now - lastActivityRef.current;
      if (shouldRefreshSession(idleMs, now - lastRefreshRef.current)) {
        void refreshToken();
      }
    }, 60 * 1000);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledActivity);
      });
      if (throttleTimeout) clearTimeout(throttleTimeout);
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
    };
  }, [user, handleActivity, refreshToken]);

  return {
    refreshToken,
    lastActivity: lastActivityRef.current,
  };
}
